import React, { useCallback, useEffect, useState } from "react";
import { Userpilot } from "userpilot";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { SocketProvider } from "../components";
import { useModals, useNavigate, useRegisterSWHook, useUrlHooks } from "../hooks";
import { useWindowResizeHook } from "../hooks/shared-hooks/use-window-resize-hook";
import { LoaderPage } from "../pages";
import {
  useTokenSelector,
  useAuthActions,
  useUserPasswordReminderSelector,
  useUserPasswordExpiredSelector,
  useUserActions,
  useOnboardingStatusSelector,
  useUserRoleSelector
} from "store";
import { getDomain, getProtocol } from "../utils/auth";
import { ProtectedRoutes } from "./Protected-routes/ProtectedRoutes";
import { PublicRoutes } from "./Public-routes/PublicRoutes";
import { OnboardingRoutes } from "./Onboarding-routes/OnboardingRoutes";
import { UrlQueryParams } from "store/types";
import { RoleName } from "../constants";
import queryString from "query-string";
const domain = getDomain();
const protocol = getProtocol();

export const Root: React.FC = (): JSX.Element => {
  const token = useTokenSelector();
  const changePasswordReminder: boolean = useUserPasswordReminderSelector();
  const expiredPassword: boolean = useUserPasswordExpiredSelector();
  const onboardingComplete = useOnboardingStatusSelector();
  const role = useUserRoleSelector();

  const canOnboard = role?.name === RoleName.Admin || role?.name === RoleName.Analyst;

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const location = useLocation();
  const { queryParams } = useUrlHooks();
  const { registerLoading } = useRegisterSWHook("/custom-worker.1.0.js", token);
  useWindowResizeHook();
  const navigate = useNavigate();
  const { sessionAction, setAuthCookieAction } = useAuthActions();
  const { resetPasswordReminderAction } = useUserActions();
  const { expiredPasswordModal } = useModals();

  const updateSession = useCallback(async () => {
    try {
      const filteredEmail = location.search.replace(/(email.+)\+/g, "%2B");
      const queryParams: UrlQueryParams = queryString.parseUrl(filteredEmail).query;

      if (token) return;
      await sessionAction(Object.keys(queryParams) as Array<keyof UrlQueryParams>);
    } catch (_e) {
      if (queryParams.auto_login_token) {
        await setAuthCookieAction(queryParams.auto_login_token);
        await sessionAction(Object.keys(queryParams) as Array<keyof UrlQueryParams>);
      }
    } finally {
      setTimeout(() => setIsLoading(false), 100);
    }
  }, [token, queryParams]);

  useEffect(() => {
    if (expiredPassword) {
      navigate({ route: "changePassword" });
    }
    if (changePasswordReminder && !expiredPassword) {
      expiredPasswordModal({
        isShow: changePasswordReminder,
        onContinue: () => resetPasswordReminderAction()
      });
    }
  }, [changePasswordReminder, expiredPassword]);

  useEffect(() => {
    updateSession();
  }, []);

  useEffect(() => {
    if (location) {
      Userpilot.reload();
    }
  }, [location]);

  if (isLoading) {
    return <LoaderPage loading={isLoading || registerLoading} />;
  }

  return (
    <>
      {!token ? (
        <PublicRoutes />
      ) : !onboardingComplete && canOnboard ? (
        <OnboardingRoutes />
      ) : (
        <SocketProvider uri={`${protocol}${domain}/api/broadcasting/auth`}>
          <ProtectedRoutes />
        </SocketProvider>
      )}
      <ToastContainer position="bottom-left" />
    </>
  );
};
